import React from 'react';

const Section2MultiModel = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-purple-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🧠</span>
      <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Multi-Model AI Engine
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">3 LLMs running in parallel, the heart of VowAI</p>

    {/* Model comparison cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
      {[
        {
          name: 'Gemini 2.0 Flash',
          provider: 'Google',
          gradient: 'from-blue-500 to-purple-500',
          bg: 'from-blue-50 to-purple-50/50',
          border: 'blue',
          strengths: ['Highest quality score (90.4 avg)', 'Strong structured output', 'Mid-range cost ($0.007/req)'],
          uses: 'Dream Wedding Generator · Moodboard Matcher',
          icon: '📊'
        },
        {
          name: 'Claude 3.5 Sonnet',
          provider: 'Anthropic',
          gradient: 'from-orange-500 to-amber-500',
          bg: 'from-orange-50 to-amber-50/50',
          border: 'orange',
          strengths: ['Most creative descriptions', 'Best cultural sensitivity', 'Rich descriptive output'],
          uses: 'Dream Wedding Generator · Moodboard Matcher',
          icon: '🎨'
        },
        {
          name: 'GPT-4o-mini',
          provider: 'OpenAI',
          gradient: 'from-green-500 to-emerald-500',
          bg: 'from-green-50 to-emerald-50/50',
          border: 'green',
          strengths: ['Fastest (793ms best) and cheapest ($0.002/req)', 'Function calling for VowBot agent', 'Embedding generation (1536-dim)'],
          uses: 'Dream Wedding Generator · Moodboard Matcher · VowBot · Embeddings',
          icon: '⚡'
        }
      ].map((model, i) => (
        <div key={i} className={`bg-gradient-to-br ${model.bg} rounded-2xl p-5 border border-${model.border}-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${model.gradient} text-white shadow-sm`}>
              {model.provider}
            </span>
            <span className="text-2xl">{model.icon}</span>
          </div>
          <h3 className="font-bold text-gray-800 text-sm mb-3">{model.name}</h3>
          <div className="space-y-1.5 mb-3">
            {model.strengths.map((s, j) => (
              <div key={j} className="flex items-start">
                <span className="text-green-500 text-xs mr-1.5 mt-0.5">✓</span>
                <span className="text-sm text-gray-600">{s}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200/50 pt-2">
            <p className="text-xs text-gray-400 font-medium">Used in: {model.uses}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Generation modes */}
    <div className="bg-gradient-to-br from-purple-50/50 to-pink-50/50 rounded-2xl p-6 border border-purple-200/30 mb-6">
      <h3 className="text-base font-bold text-purple-600 uppercase tracking-wider mb-5 text-center">Generation Modes</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            mode: 'Single Model',
            icon: '🎯',
            desc: 'User picks one model. Theme generated with that model\'s prompt strategy. If the API fails, user is notified to try another model. No silent fallbacks.',
            flow: 'User Input → Selected Model → Theme Output'
          },
          {
            mode: 'Compare Mode',
            icon: '⚖️',
            desc: 'All 3 models generate simultaneously via Promise.allSettled(). User sees side-by-side results in tabs. Partial results shown if one model fails.',
            flow: 'User Input → [Gemini + Claude + OpenAI] → 3 Themes'
          }
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-purple-100/50 text-center">
            <span className="text-2xl block mb-2">{item.icon}</span>
            <h4 className="font-bold text-gray-800 text-sm mb-2">{item.mode}</h4>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">{item.desc}</p>
            <div className="bg-purple-50 rounded-lg px-3 py-2">
              <p className="text-xs font-mono text-purple-600">{item.flow}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Engineering details */}
    <div className="bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-2xl p-5 border border-purple-200/30">
      <h3 className="text-base font-bold text-purple-600 uppercase tracking-wider mb-3">🎯 Engineering Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Parallel execution', a: 'Compare Mode uses Promise.allSettled(). All 3 models run simultaneously. If one fails, the other two still return results. No single point of failure.' },
          { q: 'Consistent output format', a: 'Each model has a tailored prompt that enforces the same JSON schema: theme_name, description, colors[], style_keywords[], key_elements[], mood, season, formality.' },
          { q: 'Per-model state management', a: 'Each model tab tracks its own save state, vendor banner visibility, and feedback independently. Tab switching preserves state via keyed objects.' },
          { q: 'A/B test integration', a: 'Each generation request checks for active experiments. The assigned prompt variant is injected before the model call. Dream Wedding Generator: Few-Shot / Context-Aware / Hybrid, Moodboard Matcher: Few-Shot / Theatrical / Professional.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-purple-100/50">
            <p className="text-sm font-bold text-purple-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section2MultiModel;
